import React from 'react';
import { ReportSection } from '../framework';
import { SectionProps } from './types';

export const IntroSection: React.FC<Pick<SectionProps, 'assumptions' | 'formatCurrency'>> = ({
  assumptions,
  formatCurrency
}) => {
  return (
    <ReportSection id="intro" title="Overview" subtitle="What this analysis covers">
      <p className="text-lg text-gray-700 mb-6">
        You own a house in Newport Beach, California—one of the most expensive real estate markets in the country.
        The house is worth about {formatCurrency(assumptions.propertyValue)}. You don't live in it. What should you do with it?
      </p>

      <p className="text-gray-700 mb-6">
        The two main options are straightforward: rent it out and become a landlord, or sell it and invest
        the proceeds in the stock market. But figuring out which one is actually better? That depends on
        a bunch of assumptions about the future—assumptions that matter a lot and that nobody can predict
        with confidence.
      </p>

      <p className="text-gray-700 mb-6">
        Will Newport Beach real estate keep appreciating at its recent pace? Will the stock market deliver
        its historical average returns, or are we headed for a rough decade like the 2000s? What happens
        if you get a problem tenant? What if California changes its property tax rules?
      </p>

      <p className="text-gray-700 mb-6">
        This analysis doesn't pretend to know the answers to those questions. Instead, it does three things:
      </p>

      <ol className="list-decimal list-inside text-gray-700 mb-6 space-y-2">
        <li><strong>Background:</strong> How real estate and stock market returns actually work—the mechanics and historical patterns</li>
        <li><strong>Numbers:</strong> What the math looks like for this specific property under various scenarios</li>
        <li><strong>Frameworks:</strong> How to think about the decision when you can't predict the future</li>
      </ol>

      <p className="text-gray-700 mb-6">
        By the end, you won't have a definitive answer (no one can give you that), but you'll understand
        the tradeoffs well enough to make a decision you can stand behind.
      </p>

      <p className="text-gray-600 text-sm italic">
        This was your mother's house. That carries meaning no spreadsheet can capture. This analysis
        focuses on the financial question, but the emotional weight of the decision is real and valid.
      </p>
    </ReportSection>
  );
};
